import {
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  Popover,
  Typography, ListItemButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { TextButton } from 'components/TextButton';
import { TFunction } from 'i18next';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { infoUpdateType } from 'types/notifications';
import type { PermissionState } from 'utils/notifications';
import { buildSentence } from 'utils/notifications/content';
import { getInitials } from 'utils/notifications/initials';
import { formatElapsed, getElapsed } from 'utils/notifications/time';

const TICK_MS = 60000;
const COARSE_POINTER = '@media (hover: none) and (pointer: coarse)';

const useStyles = makeStyles((theme: any) => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    width: `min(${theme.spacing(45)}, calc(100vw - ${theme.spacing(4)}))`,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [COARSE_POINTER]: { '& button': { minHeight: 44 } },
  },
  permissionRow: {
    flexShrink: 0,
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.background.default,
    [COARSE_POINTER]: { '& button': { minHeight: 44 } },
  },
  text: { color: theme.palette.text.primary },
  listRegion: {
    flex: '1 1 auto',
    minHeight: 0,
    maxHeight: `min(${theme.spacing(60)}, 70vh)`,
    overflowY: 'auto',
  },
  empty: {
    padding: theme.spacing(4, 2),
    textAlign: 'center',
  },
  emptyHint: { marginTop: theme.spacing(0.5) },
  row: {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.neutraldivider}`,
    '&:last-child': { borderBottom: 0 },
    [COARSE_POINTER]: { minHeight: 44 },
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.dualText.primary,
    fontSize: theme.typography.body2.fontSize,
  },
  content: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  sentence: {
    color: theme.palette.text.primary,
    fontWeight: 400,
    overflowWrap: 'break-word',
  },
  sentenceUnread: { fontWeight: 500 },
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  chip: {
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    fontSize: theme.typography.caption.fontSize,
  },
  unreadDot: {
    flexShrink: 0,
    width: theme.spacing(1),
    height: theme.spacing(1),
    marginLeft: theme.spacing(1),
    borderRadius: '50%',
    backgroundColor: theme.palette.info.main,
  },
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
  },
}));

type NotificationRowProps = {
  notification: infoUpdateType;
  now: number;
  t: TFunction;
  onSelect: (notification: infoUpdateType) => void;
};

const NotificationRow: FC<NotificationRowProps> = ({
  notification, now, t, onSelect,
}) => {
  const classes = useStyles();
  const {
    name, kind, orderCode, timestamp, viewed,
  } = notification;

  return (
    <ListItemButton className={classes.row} onClick={() => onSelect(notification)}>
      <ListItemAvatar>
        <Avatar aria-hidden="true" className={classes.avatar}>{getInitials(name)}</Avatar>
      </ListItemAvatar>
      <div className={classes.content}>
        <Typography variant="body2" className={clsx(classes.sentence, !viewed && classes.sentenceUnread)}>
          {buildSentence(t, { name, kind })}
        </Typography>
        <div className={classes.meta}>
          {orderCode && <Chip size="small" variant="outlined" label={orderCode} className={classes.chip} />}
          <Typography variant="caption" className={classes.text}>
            {formatElapsed(t, getElapsed(now, timestamp))}
          </Typography>
        </div>
      </div>
      {!viewed && <span role="img" aria-label={t('notifications:unread-label')} className={classes.unreadDot} />}
      {orderCode && <span className={classes.visuallyHidden}>{t('notifications:open-order', { code: orderCode })}</span>}
    </ListItemButton>
  );
};

type NotificationsPopupProps = {
  openNotifications: boolean;
  anchorNotifications: HTMLElement | null;
  handleCloseNotifications: () => void;
  notifications: infoUpdateType[];
  t: TFunction;
  permission?: PermissionState;
  onEnableDesktop?: () => void;
  onClear?: () => void;
  onOpenOrder?: (orderId: string) => void;
};

const noop = () => {};

const NotificationsPopup: FC<NotificationsPopupProps> = ({
  openNotifications,
  anchorNotifications,
  handleCloseNotifications,
  notifications,
  t,
  permission = 'unsupported',
  onEnableDesktop = noop,
  onClear = noop,
  onOpenOrder = noop,
}) => {
  const classes = useStyles();
  const [now, setNow] = useState(Date.now);

  // The popover stays mounted while closed, so refresh the clock on every open and keep it ticking only while open.
  useEffect(() => {
    if (!openNotifications) return undefined;

    const intervalId = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(intervalId);
  }, [openNotifications]);

  const handleSelect = (notification: infoUpdateType) => {
    const orderId = notification.update?._id;
    if (orderId) onOpenOrder(orderId);
    handleCloseNotifications();
  };

  const permissionRows: Record<PermissionState, ReactNode> = {
    default: <TextButton text={t('notifications:enable-desktop')} icon="bell" buttonColor="primary" size="small" onClick={() => onEnableDesktop()} />,
    denied: <Typography variant="caption" className={classes.text}>{t('notifications:desktop-blocked')}</Typography>,
    granted: null,
    unsupported: null,
  };
  const permissionRow = permissionRows[permission];

  return (
    <Popover
      open={openNotifications}
      anchorEl={anchorNotifications}
      onClose={handleCloseNotifications}
      TransitionProps={{ onEnter: () => setNow(Date.now()) }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      classes={{ paper: classes.paper }}
    >
      <div className={classes.header}>
        <Typography variant="subtitle2" className={classes.text}>{t('notifications:popover-title')}</Typography>
        {notifications.length > 0 && (
          <TextButton text={t('notifications:clear-all')} icon="trash-alt" buttonColor="primary" size="small" onClick={() => onClear()} />
        )}
      </div>
      {permissionRow && <div className={classes.permissionRow}>{permissionRow}</div>}
      <div className={classes.listRegion}>
        {notifications.length > 0 ? (
          <List disablePadding>
            {notifications.slice().reverse().map((notification, index) => (
              <NotificationRow
                key={notification.id ?? `${notification.timestamp}-${index}`}
                notification={notification}
                now={now}
                t={t}
                onSelect={handleSelect}
              />
            ))}
          </List>
        ) : (
          <div className={classes.empty}>
            <Typography variant="body2" className={classes.text}>{t('notifications:empty-history')}</Typography>
            <Typography variant="caption" display="block" className={clsx(classes.text, classes.emptyHint)}>
              {t('notifications:empty-hint')}
            </Typography>
          </div>
        )}
      </div>
    </Popover>
  );
};

export default NotificationsPopup;
